import React from 'react';

import { SalesChannelType } from '@fd/customHooks/useQueryRMSSalesChannels';
import AccessTimeFilledOutlinedIcon from '@mui/icons-material/AccessTimeFilledOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import MopedOutlinedIcon from '@mui/icons-material/MopedOutlined';
import MoreTimeOutlinedIcon from '@mui/icons-material/MoreTimeOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import SettingsApplicationsOutlinedIcon from '@mui/icons-material/SettingsApplicationsOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import StoreOutlined from '@mui/icons-material/StoreOutlined';
import Warning from '@mui/icons-material/Warning';
import WebOutlinedIcon from '@mui/icons-material/WebOutlined';
import ZoomInMapOutlinedIcon from '@mui/icons-material/ZoomInMapOutlined';
import { salesChannelPermissions } from 'components/SalesChannels/permissions';

import { SettingsMenuItem } from '@fd/ui/List/SettingsList';
import { getSalesChannelType } from '@fd/ui/utils/generateSalesChannelsTypes';

import { toKebabCase } from '../../../helpers/strings';
import { getPhoneNumbersMenuItem } from '../../PhoneAgent/phoneNumbersPage';
import { RMSModules } from './rmsModules';

// Pre-computed Sets for O(1) lookups instead of O(n) array.includes()
const MARKETPLACE_CHANNELS = new Set([
  SalesChannelType.DELIVEROO,
  SalesChannelType.UBER_EATS,
  SalesChannelType.JUST_EATS,
]);

const OPENING_HOURS_CHANNELS = new Set([
  SalesChannelType.FLIPDISH_KIOSK,
  SalesChannelType.FLIPDISH_MOBILE_APP,
  SalesChannelType.FLIPDISH_WEB_APP,
  SalesChannelType.DELIVEROO,
  SalesChannelType.JUST_EATS,
  SalesChannelType.UBER_EATS,
]);

const FLIPDISH_CHANNELS = new Set([
  SalesChannelType.FLIPDISH_KIOSK,
  SalesChannelType.FLIPDISH_MOBILE_APP,
  SalesChannelType.FLIPDISH_WEB_APP,
]);

const ORDER_INGEST_CHANNELS = new Set([
  SalesChannelType.UBER_EATS,
  SalesChannelType.JUST_EATS,
  SalesChannelType.DELIVEROO,
  SalesChannelType.FLIPDISH_KIOSK,
  SalesChannelType.FLIPDISH_MOBILE_APP,
  SalesChannelType.FLIPDISH_WEB_APP,
  SalesChannelType.EXTERNAL_APP,
]);

// Pre-computed kebab case strings to avoid repeated calls
const KEBAB_CASE_MODULES = {
  OPERATING_CONFIGURATION: toKebabCase(RMSModules.OPERATING_CONFIGURATION),
  PAYOUT_CONFIGURATION: toKebabCase(RMSModules.PAYOUT_CONFIGURATION),
  OPENING_HOURS_OVERRIDES: toKebabCase(RMSModules.OPENING_HOURS_OVERRIDES),
  DELIVERY_CHARGES: toKebabCase(RMSModules.DELIVERY_CHARGES),
  TIP_CONFIGURATION: toKebabCase(RMSModules.TIP_CONFIGURATION),
  ORDER_INGEST: toKebabCase(RMSModules.ORDER_INGEST),
  TIMINGS_CONFIGURATION: toKebabCase(RMSModules.TIMINGS_CONFIGURATION),
  OVER_PHONE_PAYMENT: toKebabCase(RMSModules.OVER_PHONE_PAYMENT),
  WEB_APP_CONFIGURATION: toKebabCase(RMSModules.WEB_APP_CONFIGURATION),
  MOBILE_APP_CONFIGURATION: toKebabCase(RMSModules.MOBILE_APP_CONFIGURATION),
};

// Menu item templates to reduce object creation
const MENU_ITEM_TEMPLATES: Record<string, SettingsMenuItem> = {
  GENERAL_SETTINGS: {
    title: 'General_settings',
    path: '/edit',
    icon: <SettingsOutlinedIcon />,
    subtitle: 'Set_name_price_bands_and_menu_View_associated_bran',
    searchAlias: [
      'Channel name',
      'sales channel name',
      'assign menu',
      'price bands',
      'sales channel type',
      'brand sales channel',
      'phone number sales channel',
      'store ID',
      'edit restaurant name online order',
    ],
  },
  OPERATING_CONFIGURATION: {
    title: 'Operating_Configuration',
    path: `/${KEBAB_CASE_MODULES.OPERATING_CONFIGURATION}`,
    icon: <SettingsApplicationsOutlinedIcon />,
    subtitle: 'Configure_operating_configuration',
    permissions: ['EditStores'],
    searchAlias: [
      'Commission run schedule',
      'Commission rate',
      'Active queue activation time',
      'Dispatch options',
      'Delivery',
      'Rewards menu',
      'Enable Dine In',
      'Enable Take Away',
      'Enable Collection',
      'Enable Delivery',
      'Use own delivery driver',
      'Use own delivery rider',
      'Rewards menu',
      'Price band rewards menu',
    ],
  },
  COLLECTION_SETTINGS: {
    title: 'Collection_Settings',
    path: `/settings/collection`,
    icon: <StoreOutlined />,
    subtitle: 'Collection_settings_description',
    permissions: ['EditStores'],
    searchAlias: [
      'Enable collection',
      'Collection configuration online order',
      'Collection settings online order',
      'Collection settings app',
      'Collection settings web',
      'Require customer name when ordering',
      'Collection minimum order price',
      'Collection minimum order amount',
      'Set collection fee online order',
      'Accept cash for collection',
      'Lead time settings',
      'SMS message for estimated collection time',
      'Time to order accepted online order',
      'Web collection settings',
      'App collection settings',
      'Configure website collection',
      'Configure app collection',
    ],
  },
  PAYOUT_CONFIGURATION: {
    title: 'Payout_Configuration',
    path: `/${KEBAB_CASE_MODULES.PAYOUT_CONFIGURATION}`,
    icon: <CreditCardOutlinedIcon />,
    subtitle: 'Configure_payout_configuration',
    tagText: 'Flipdish_staff_only',
    permissions: ['FlipdishStaff'],
  },
  BANKING: {
    title: 'Banking_title',
    path: '/bankingsettings',
    icon: <AccountBalanceOutlined />,
    subtitle: 'Add_and_edit_bank_account_connected_to_this_sales',
    permissions: ['ViewAssignedBankAccount'],
    searchAlias: [
      'Assign bank account to sales channel',
      'Assign bank account to restaurant',
      'Manage bank account',
      'Update bank account online order',
      'Update bank account apps',
    ],
  },
  OPENING_HOURS: {
    title: 'Opening_Hours',
    path: '/opening-hours-v2',
    icon: <AccessTimeOutlinedIcon />,
    subtitle: 'Configure_sales_channel_opening_hours',
    searchAlias: [
      'Collection hours',
      'delivery hours',
      'sales channel opening hours',
      'change hours online order',
      'change opening hours app',
    ],
  },
  OPENING_HOURS_OVERRIDES: {
    title: 'Opening_Hours_Overrides',
    path: `/${KEBAB_CASE_MODULES.OPENING_HOURS_OVERRIDES}`,
    icon: <AccessTimeFilledOutlinedIcon />,
    subtitle: 'Configure_sales_channel_opening_hours_overrides',
    permissions: ['EditStores'],
    searchAlias: [
      'Override collection hours',
      'override delivery hours',
      'temporarily change opening hours',
      'temporarily close restaurant',
      'close location',
      'open a restaurant',
      'set closing time',
      'allow collection preorders while sales channel closed',
    ],
  },
  DELIVERY_CHARGES: {
    title: 'Delivery_Charges',
    path: `/${KEBAB_CASE_MODULES.DELIVERY_CHARGES}`,
    icon: <MopedOutlinedIcon />,
    subtitle: 'Configure_delivery_charges',
    permissions: ['EditStores'],
    searchAlias: [
      'Delivery fees',
      'Delivery configuration POS',
      'Delivery settings POS',
      'Delivery charges POS',
      'Delivery settings Point of Sale',
      'Staggered delivery charges',
      'Delivery distances POS',
      'Fixed delivery charge',
      'VAT percentage delivery',
      'Max delivery distance',
      'Minimum delivery distance',
      'Delivery maximum order price POS',
      'Delivery minimum order amount POS',
    ],
  },
  TIP_CONFIGURATION: {
    title: 'Tip_Configuration',
    path: `/${KEBAB_CASE_MODULES.TIP_CONFIGURATION}`,
    icon: <AttachMoneyOutlinedIcon />,
    subtitle: 'Configure_tip_configuration',
    permissions: ['EditStores'],
    searchAlias: [
      'Tips settings POS',
      'Tip settings Point of Sale',
      'Tips fixed amount',
      'Tips percentage of bill amount POS',
      'Cut off bill amount POS',
    ],
  },
  ORDER_INGEST: {
    title: 'Order_Ingest',
    path: `/${KEBAB_CASE_MODULES.ORDER_INGEST}`,
    icon: <ZoomInMapOutlinedIcon />,
    subtitle: 'Configure_order_ingest',
    tagText: 'Flipdish_staff_only',
    permissions: ['FlipdishStaff'],
  },
  TIMINGS_CONFIGURATION: {
    title: 'Timings_Configuration',
    path: `/${KEBAB_CASE_MODULES.TIMINGS_CONFIGURATION}`,
    icon: <MoreTimeOutlinedIcon />,
    subtitle: 'Configure_timings_configuration',
    permissions: ['EditStores'],
    searchAlias: [
      'Default prep time',
      'Default delivery time',
      'Order interval',
      'Timing overrides',
      'Prep time',
      'Delivery time',
      'Dispatch type',
      'Order price',
      'Auto accept',
      'Remove timing overrides',
    ],
  },
  OVER_PHONE_PAYMENT: {
    title: 'Over_Phone_Payment',
    path: `/${KEBAB_CASE_MODULES.OVER_PHONE_PAYMENT}`,
    icon: <LocalPhoneOutlinedIcon />,
    subtitle: 'Configure_over_phone_payment',
    permissions: ['EditStores'],
    searchAlias: [
      'POS over the phone payment',
      'Over the phone payment POS',
      'Enable over the phone payment',
    ],
  },
  WEB_APP_CONFIGURATION: {
    title: 'Web_App_Configuration',
    path: `/${KEBAB_CASE_MODULES.WEB_APP_CONFIGURATION}`,
    icon: <WebOutlinedIcon />,
    subtitle: 'Configure_web_app_configuration',
    permissions: ['EditStores'],
  },
  MOBILE_APP_CONFIGURATION: {
    title: 'Mobile_App_Configuration',
    path: `/${KEBAB_CASE_MODULES.MOBILE_APP_CONFIGURATION}`,
    icon: <PhoneIphoneOutlinedIcon />,
    subtitle: 'Configure_mobile_app_configuration',
    permissions: ['EditStores'],
  },
  FEE_CONFIG: {
    title: 'View_and_Edit_Fees',
    path: '',
    icon: <AttachMoneyOutlinedIcon />,
    subtitle: 'Configure_fees_for_this_sales_channel',
    tagText: 'Flipdish_staff_only',
    permissions: ['FlipdishStaff'],
  },
  DANGER_ZONE: {
    title: 'Danger_zone_title',
    path: '/dangerzone',
    icon: <Warning />,
    subtitle: 'Danger_zone_description_2',
    isDangerZone: true,
    permissions: ['Owner', 'FlipdishStaff'],
    searchAlias: [
      'Delete sales channel',
      'Delete store',
      'Archive store',
      'Archive sales channel',
      'Unpublish sales channel',
      'Unpublish online order',
      'Unpublish app restaurant option',
    ],
  },
};

const MARKETPLACE_MANAGED_ITEM_TITLES = new Set([
  MENU_ITEM_TEMPLATES.GENERAL_SETTINGS.title,
  MENU_ITEM_TEMPLATES.OPENING_HOURS.title,
  MENU_ITEM_TEMPLATES.ORDER_INGEST.title,
  MENU_ITEM_TEMPLATES.DANGER_ZONE.title,
]);

// Helper function to create menu items with common data
const createMenuItem = (
  template: SettingsMenuItem,
  baseUrl: string,
  commonData: {
    salesChannelType?: string;
    salesChannelName?: string;
    salesChannelId?: string;
    permissions?: AppState['permissions'];
  }
): SettingsMenuItem => ({ ...commonData, ...template, path: `${baseUrl}${template.path}` });

export const getSalesChannelMenuItems = ({
  appId,
  baseUrl,
  salesChannelType,
  storefrontInformation,
  propertyId,
  isSearchItems = false,
  isShowCollectionSettingsAtPropertyLevelOn = false,
  isPhoneNumbersPageOn = false,
  storeId,
  onMarketplaceManageClick,
}: {
  appId: string;
  baseUrl: string;
  salesChannelType: SalesChannelType;
  storefrontInformation: any;
  propertyId: string;
  isSearchItems?: boolean;
  isShowCollectionSettingsAtPropertyLevelOn?: boolean;
  isPhoneNumbersPageOn?: boolean;
  storeId?: string;
  /** Uber Eats and Deliveroo direct integrations open the marketplace-integrations Manage page instead */
  onMarketplaceManageClick?: () => void;
}) => {
  const commonSalesChannelData = {
    salesChannelType: getSalesChannelType(salesChannelType, false),
    salesChannelName: storefrontInformation?.name,
    salesChannelId: storefrontInformation?.code,
    permissions: salesChannelPermissions,
  };

  const menuItems: SettingsMenuItem[] = [];

  // Always include these items
  menuItems.push(
    createMenuItem(MENU_ITEM_TEMPLATES.GENERAL_SETTINGS, baseUrl, commonSalesChannelData)
  );
  if (isPhoneNumbersPageOn && propertyId) {
    menuItems.push(getPhoneNumbersMenuItem({ appId, propertyId, ...commonSalesChannelData }));
  }
  menuItems.push(
    createMenuItem(MENU_ITEM_TEMPLATES.OPERATING_CONFIGURATION, baseUrl, commonSalesChannelData)
  );
  menuItems.push(
    createMenuItem(MENU_ITEM_TEMPLATES.DELIVERY_CHARGES, baseUrl, commonSalesChannelData)
  );
  if (isShowCollectionSettingsAtPropertyLevelOn) {
    menuItems.push(
      createMenuItem(MENU_ITEM_TEMPLATES.COLLECTION_SETTINGS, baseUrl, commonSalesChannelData)
    );
  }
  menuItems.push(
    createMenuItem(MENU_ITEM_TEMPLATES.TIMINGS_CONFIGURATION, baseUrl, commonSalesChannelData)
  );

  // Conditional items based on channel type
  if (salesChannelType === SalesChannelType.POS) {
    menuItems.push(
      createMenuItem(MENU_ITEM_TEMPLATES.PAYOUT_CONFIGURATION, baseUrl, commonSalesChannelData)
    );
    menuItems.push(
      createMenuItem(MENU_ITEM_TEMPLATES.TIP_CONFIGURATION, baseUrl, commonSalesChannelData)
    );
    menuItems.push(
      createMenuItem(MENU_ITEM_TEMPLATES.OVER_PHONE_PAYMENT, baseUrl, commonSalesChannelData)
    );
    // Terminal Customization for POS with credit card terminals
    if (storefrontInformation?.isUsingCreditCardTerminals) {
      menuItems.push({
        title: 'Sales_channel_customise_terminal_title',
        path: `/${appId}/marketing/${propertyId}/stripe-location/${storefrontInformation.locationId}/branding`,
        icon: <SmartphoneIcon />,
        subtitle: 'Sales_channel_customise_terminal_description',
        tagText: 'Flipdish_staff_only',
        ...commonSalesChannelData,
        permissions: ['FlipdishStaff'],
      });
    }
  }

  // Banking for non-marketplace platforms
  if (!MARKETPLACE_CHANNELS.has(salesChannelType)) {
    menuItems.push(createMenuItem(MENU_ITEM_TEMPLATES.BANKING, baseUrl, commonSalesChannelData));
  }

  // Opening Hours for specific channel types
  if (OPENING_HOURS_CHANNELS.has(salesChannelType)) {
    menuItems.push(
      createMenuItem(MENU_ITEM_TEMPLATES.OPENING_HOURS, baseUrl, commonSalesChannelData)
    );
  }

  // Opening Hours Overrides for Flipdish channels
  if (FLIPDISH_CHANNELS.has(salesChannelType)) {
    menuItems.push(
      createMenuItem(MENU_ITEM_TEMPLATES.OPENING_HOURS_OVERRIDES, baseUrl, commonSalesChannelData)
    );
  }

  // Order Ingest for specific channel types
  if (ORDER_INGEST_CHANNELS.has(salesChannelType)) {
    menuItems.push(
      createMenuItem(MENU_ITEM_TEMPLATES.ORDER_INGEST, baseUrl, commonSalesChannelData)
    );
  }

  // Channel-specific configurations
  if (salesChannelType === SalesChannelType.WEB_APP) {
    menuItems.push(
      createMenuItem(MENU_ITEM_TEMPLATES.WEB_APP_CONFIGURATION, baseUrl, commonSalesChannelData)
    );
  }

  if (salesChannelType === SalesChannelType.MOBILE_APP) {
    menuItems.push(
      createMenuItem(MENU_ITEM_TEMPLATES.MOBILE_APP_CONFIGURATION, baseUrl, commonSalesChannelData)
    );
  }
  // View and edit fees (Flipdish staff only)
  if (appId && storeId) {
    menuItems.push({
      ...commonSalesChannelData,
      ...MENU_ITEM_TEMPLATES.FEE_CONFIG,
      path: `/${appId}/bulk-updater/fee-config/${storeId}`,
    });
  }

  if (
    (salesChannelType === SalesChannelType.POS || MARKETPLACE_CHANNELS.has(salesChannelType)) &&
    !isSearchItems
  ) {
    menuItems.push(
      createMenuItem(MENU_ITEM_TEMPLATES.DANGER_ZONE, baseUrl, commonSalesChannelData)
    );
  }

  if (onMarketplaceManageClick) {
    return menuItems.map((item) =>
      MARKETPLACE_MANAGED_ITEM_TITLES.has(item.title)
        ? { ...item, path: '', onClick: onMarketplaceManageClick }
        : item
    );
  }
  return menuItems;
};
